import React from 'react'
import { useGenericReducer } from '../../hooks/generic-hook'
import {
  PaginationActions,
  PaginationActionType,
  reducerPage,
} from '../../hooks/pagination-reducer'
import useTenantContext from '../context/tenant.context'
import { useForm } from 'react-hook-form'
import { EntityType } from '../../shared/models/entity-type.models'
import KopiliotList from '../../components/kopiliot-list/KopiliotList'
import { RootState, useAppSelector } from '../../store/store'
import { Authority } from '../../shared/models/user'
import { useNavigate } from 'react-router-dom'
import { ApiKeyID } from '../../shared/models/id/api-key-id'
import ApiKeySearchForm from '../../features/api-key/form/ApiKeySearchForm'
import { ApiKeyQuery } from '../../store/kopiliot-api/query-type'
import ApiKey from '../../shared/models/api-key.model'
import { useLazyGetApiKeysQuery } from 'store/kopiliot-api/api-key'
import {
  defaultApiKeyActionsRow,
  getColumnsForTableApiKey,
} from '../../features/api-key/utils'
import AddApiKeyModal from '../../features/api-key/modal/AddApiKeyModal'

interface ApiKeyListProps {
  linkTo?: string
}

const ApiKeyList = ({ linkTo }: ApiKeyListProps) => {
  const navigate = useNavigate()
  // Get the current user
  const user = useAppSelector((state: RootState) => state.currentUser)
  // Get the context of the current tenant and customer
  const { tenantIDParam } = useTenantContext()
  // Modal Delete
  const [showModalDelete, setShowModalDelete] = React.useState(false)
  const [selectedApiKey, setSelectedApiKey] = React.useState<ApiKeyID>()
  // Pagination
  const [paginationState, dispatchPaginationAction] = useGenericReducer<
    typeof reducerPage,
    ApiKeyQuery,
    PaginationActions
  >(reducerPage, {
    limit: 20,
    offset: 0,
    textSearch: '',
    tenantID: undefined,
  })

  const searchForm = useForm<ApiKeyQuery>()
  const {
    watch,
    formState: { errors },
  } = searchForm
  const { textSearch, tenantID } = watch()
  React.useEffect(() => {
    if (tenantIDParam) {
      dispatchPaginationAction({
        type: PaginationActionType.PAGE_FILTER,
        payload: { filter: textSearch },
      })
    } else {
      dispatchPaginationAction({
        type: PaginationActionType.PAGE_FILTER,
        payload: { filter: textSearch },
      })
    }
  }, [tenantIDParam, tenantID, textSearch])
  React.useEffect(() => {
    fetchApiKeys()
  }, [paginationState])
  const [getApiKeys, resultGetApiKeys] = useLazyGetApiKeysQuery()
  //const [getApiKeysByTenants, resultGetApiKeysByTenants] = useLazyGetTenantApiKeys()
  // Modal Create
  const [showModalCreate, setShowModalCreate] = React.useState(false)

  // utility function
  const fetchApiKeys = () => {
    if (tenantIDParam) {
      getApiKeys({
        limit: paginationState.limit,
        offset: paginationState.offset,
        textSearch: paginationState.textSearch,
        sortOrder: paginationState.sortOrder,
        tenantID: { id: tenantIDParam, entityType: EntityType.TENANT },
      })
    } else {
      getApiKeys({
        limit: paginationState.limit,
        offset: paginationState.offset,
        textSearch: paginationState.textSearch,
        sortOrder: paginationState.sortOrder,
        tenantID: tenantID,
      })
    }
  }
  const isLoadingData = resultGetApiKeys.isLoading
  const getDisabledField = (): Array<keyof ApiKeyQuery> => {
    return tenantIDParam ? ['tenantID'] : []
  }
  const getData = () => {
    if (tenantIDParam) {
      return resultGetApiKeys.data
    } else {
      return resultGetApiKeys.data
    }
  }
  const onDeleteItem = (id: string) => {
    setShowModalDelete(true)
    setSelectedApiKey({ id: id, entityType: EntityType.DASHBOARD })
  }
  const onEditItem = (id: string) => {
    setShowModalCreate(true)
  }

  const actionsRow = defaultApiKeyActionsRow(onDeleteItem, onEditItem)
  const getHiddenColumns = (): Array<keyof ApiKey> => {
    if (user.authority === Authority.TENANT_ADMIN || tenantIDParam) {
      return ['tenantID']
    } else if (user.authority === Authority.CUSTOMER_USER) {
      return ['tenantID']
    } else {
      return []
    }
  }
  return (
    <>
      <KopiliotList
        title={'ApiKey'}
        icon={'chart-line'}
        isLoading={isLoadingData}
        actionsProps={{
          filterForm: (
            <ApiKeySearchForm
              form={searchForm}
              onSubmit={() => {}}
              disabledFields={getDisabledField()}
            />
          ),
          onRefresh: () => {
            fetchApiKeys()
          },
          onAdd: () => {
            setShowModalCreate(true)
          },
        }}
        data={getData()}
        paginationProps={{
          pageLimit: paginationState.limit,
          totalRecords: getData()?.totalRows || 0,
          onPageChanged: (page: {
            currentPage: number
            totalPages: number
            pageLimit: number
          }) => {
            dispatchPaginationAction({
              type: PaginationActionType.PAGE_CHANGE,
              payload: { ...page },
            })
          },
          onChangePageSize: (size: number) => {
            dispatchPaginationAction({
              type: PaginationActionType.PAGE_SIZE,
              payload: { pageSize: size },
            })
          },
        }}
        tableProps={{
          tableProps: {
            size: 'sm',
            className: 'mb-0 overflow-hidden table-hover cursor-pointer',
          },
          rowClassName: 'default__table-row align-middle fs-10',
          hiddenColumns: ['id', ...getHiddenColumns()],
          headerClassName: 'bg-200 text-900 text-nowrap align-middle',
          cellClassName: 'py-2 pe-4',
          columns: getColumnsForTableApiKey(actionsRow),
          sortOrder: paginationState.sortOrder,
          setSortOrder: (sorting) => {
            dispatchPaginationAction({
              type: PaginationActionType.PAGE_SORT,
              payload: { sort: sorting },
            })
          },
          onClickRow: (row) => {
            navigate(`${linkTo ? linkTo : ''}${row.original.id?.id}`)
          },
        }}
        cardProps={{
          idAccessor: (item: ApiKey) => item.id!.id,
          titleAccessor: (item: ApiKey) => item.projectID,
          descriptionAccessor: (item: ApiKey) => item.projectName,
          linkAccessor: (item: ApiKey) =>
            `${linkTo ? linkTo : ''}${item.id!.id}`,
          actionsRow: actionsRow,
        }}
      />
      <AddApiKeyModal isOpen={showModalCreate} setIsOpen={setShowModalCreate} />
      {/* <DeleteApiKeyModal
        isOpen={showModalDelete}
        setIsOpen={setShowModalDelete}
        dashboardID={selectedApiKey}
      />*/}
    </>
  )
}

export default ApiKeyList
